import type { AltBugConfig } from "../types";
import { construirFormData, construirPayload, type DadosReport } from "./payload";

export interface ResultadoEnvio {
  ok: boolean;
  status: number;
  /** Código humano do card criado (ex.: "BUG-42"), devolvido pelo intake no 202. */
  codigo?: string;
  /** URL pública do app, devolvida pelo intake no 202 — alimenta o link
      "acompanhar meus chamados" na tela de sucesso. */
  portalUrl?: string;
  /** Token de ACESSO ao chamado (DEV-107), devolvido pelo intake no 202. É a credencial
      que deixa o widget acompanhar e responder este chamado sem login, e o 202 é a ÚNICA
      vez que ele existe em claro (o servidor guarda só o hash) — guarde-o no storage
      antes de sair da tela, ou o acesso anônimo a este chamado se perde. */
  chamadoToken?: string;
  /** segundos sugeridos pelo servidor no 429 (Retry-After), se houver. */
  retryAfter?: number;
  /** `message` do corpo de erro (o formato `{message, errors}` do api). Serve ao 422 do
      e-mail obrigatório (DEV-103): a recusa é do servidor, então a frase que a pessoa lê tem
      de ser a dele — não uma nossa, que envelheceria em silêncio se a regra mudasse. */
  mensagem?: string;
  /** Erros POR CAMPO do 422 (`errors` do api), já achatados em `campo → 1ª mensagem`.
      As chaves vêm com PONTO (`dados.reporter.email`, `dados.tipo`, `screenshot`) — é assim
      que o Laravel as emite, e não como objeto aninhado.

      Existe porque o widget precisa saber ONDE desenhar cada recusa. Antes ele mandava todo
      422 para o campo de e-mail, e `erroDoCampo` desiste em silêncio quando o alvo não
      existe — o que acontece justamente nos hosts que informam o e-mail no init (o Tasks é
      um deles). O resultado era a pessoa clicar em Enviar, o botão reabilitar e NADA
      aparecer, com o relato inteiro preso na tela. */
  errosPorCampo?: Record<string, string>;
}

/** Envia o report ao intake público. Token só no header X-Widget-Token (nunca no
    corpo). Não faz retry automático (evita retry-storm); o chamador trata 429. */
export async function enviarReport(
  cfg: AltBugConfig,
  screenshot: Blob | null,
  dados: Omit<DadosReport, "maxConsole">,
  anexo?: File | null,
  sessao?: string | null,
): Promise<ResultadoEnvio> {
  const payload = construirPayload({ ...dados, maxConsole: cfg.consoleMaxLinhas });
  const body = construirFormData(screenshot, payload, anexo);

  // A sessão do widget (DEV-109) acompanha o REPORT desde o DEV-260: é ela que prova quem
  // está abrindo o chamado quando o relator exige login, e é dela que o servidor tira o
  // e-mail. Vai sempre que existe — relator que não exige login simplesmente a ignora, e
  // mandar condicionalmente faria o widget decidir uma regra que é do servidor.
  const headers: Record<string, string> = { "X-Widget-Token": cfg.token };
  if (typeof sessao === "string" && sessao !== "") headers["X-Chamado-Sessao"] = sessao;

  const res = await fetch(cfg.endpoint, {
    method: "POST",
    headers,
    body,
  });

  // No sucesso, o intake devolve { id, codigo, portalUrl, chamadoToken } — o código é o
  // protocolo, a portalUrl vira o link de acompanhar e o token abre o chamado no próprio
  // widget. Todos opcionais: API antiga (sem portalUrl/chamadoToken) ou app sem URL
  // configurada não quebram a tela de sucesso, só oferecem menos.
  let codigo: string | undefined;
  let portalUrl: string | undefined;
  let chamadoToken: string | undefined;
  let mensagem: string | undefined;
  let errosPorCampo: Record<string, string> | undefined;
  if (res.ok) {
    try {
      const corpo = (await res.json()) as
        | { codigo?: unknown; portalUrl?: unknown; chamadoToken?: unknown }
        | null;
      const c = corpo?.codigo;
      if (typeof c === "string" && c !== "") codigo = c;
      const p = corpo?.portalUrl;
      if (typeof p === "string" && p !== "") portalUrl = p;
      const t = corpo?.chamadoToken;
      if (typeof t === "string" && t !== "") chamadoToken = t;
    } catch {
      // resposta sem corpo JSON — segue sem código
    }
  } else {
    // Só no ERRO, e sem deixar a leitura do corpo derrubar o resultado: `status` já basta
    // para os outros casos, e a mensagem é um extra para quem sabe usá-la (o 422).
    try {
      const corpo = (await res.json()) as { message?: unknown; errors?: unknown } | null;
      const m = corpo?.message;
      if (typeof m === "string" && m !== "") mensagem = m;
      errosPorCampo = achatarErros(corpo?.errors);
    } catch {
      /* erro sem corpo JSON — segue só com o status */
    }
  }

  const ra = Number(res.headers.get("Retry-After"));
  return {
    ok: res.ok,
    status: res.status,
    codigo,
    portalUrl,
    chamadoToken,
    mensagem,
    errosPorCampo,
    retryAfter: Number.isFinite(ra) && ra > 0 ? ra : undefined,
  };
}

/**
 * `{campo: [msg, ...]}` do Laravel → `{campo: msg}`. Só a primeira mensagem de cada campo:
 * é a que o formulário tem espaço para mostrar, e é o mesmo critério do `httpClient` do
 * front. Corpo em formato inesperado devolve `undefined` — o chamador cai no genérico em
 * vez de desenhar `[object Object]` na página do cliente.
 */
function achatarErros(errors: unknown): Record<string, string> | undefined {
  if (typeof errors !== "object" || errors === null || Array.isArray(errors)) return undefined;

  const out: Record<string, string> = {};
  for (const [campo, msgs] of Object.entries(errors as Record<string, unknown>)) {
    const primeira = Array.isArray(msgs) ? msgs[0] : msgs;
    if (typeof primeira === "string" && primeira !== "") out[campo] = primeira;
  }

  return Object.keys(out).length ? out : undefined;
}
